import React, { useEffect, useMemo, useState } from "react";
import {
  Modal,
  Box,
  FormControlLabel,
  Switch,
  Typography,
  Paper,
  IconButton,
  Button,
  CircularProgress,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TextField,
  FormHelperText,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddIcon from "@mui/icons-material/Add";
import * as Yup from "yup";
import { Formik, Form } from "formik";
import { InputField } from "../../../pro/applicationForm/input";
import { CustomSelectField } from "../../../pro/applicationForm/customSelect";
import axiosInstant from "../../../../api";

const validationSchema = Yup.object().shape({
  name: Yup.string().required("Name is required"),
});

// `defaultsOnly`: the viewer may set option defaults but nothing else
// (fulfillment). Name / autopilot / content types render disabled and the
// PATCH body carries only `option_defaults` — the API rejects anything more
// from that role with 403 ChannelDefaultsOnly.
const ContentChannelModal = ({
  open,
  onClose,
  onSave,
  initialData = {},
  defaultsOnly = false,
}) => {
  const [contentTypes, setContentTypes] = useState([]);
  const [loadingContentTypes, setLoadingContentTypes] = useState(false);

  // Option defaults state
  const [channelOptions, setChannelOptions] = useState([]);
  const [loadingChannelOptions, setLoadingChannelOptions] = useState(false);
  const [optionDefaultRows, setOptionDefaultRows] = useState([]);
  const [loadingChannelDetail, setLoadingChannelDetail] = useState(false);
  const [optionDefaultsError, setOptionDefaultsError] = useState("");
  const [rowErrors, setRowErrors] = useState({});

  const fetchContentTypes = async () => {
    setLoadingContentTypes(true);
    try {
      const response = await axiosInstant.get("/content-types");
      const contentTypeData = Array.isArray(response.data)
        ? response.data
        : response.data?.rows || [];
      setContentTypes(contentTypeData);
    } catch (err) {
      console.error("Failed to fetch content types:", err);
      setContentTypes([]);
    } finally {
      setLoadingContentTypes(false);
    }
  };

  const fetchChannelOptions = async () => {
    setLoadingChannelOptions(true);
    try {
      const response = await axiosInstant.get("/content-channel-options");
      const data = Array.isArray(response.data)
        ? response.data
        : response.data?.rows || [];
      setChannelOptions(data);
    } catch (err) {
      console.error("Failed to fetch channel options:", err);
      setChannelOptions([]);
    } finally {
      setLoadingChannelOptions(false);
    }
  };

  const hydrateOptionDefaults = (defaults) => {
    const rows = (defaults || []).map((d, i) => ({
      key: `${d.id ?? `seed-${i}`}-${Date.now()}-${i}`,
      optionId: d.option_value?.option?.id ?? "",
      valueId: d.option_value?.id ?? d.content_channel_option_value_id ?? "",
      numericValue:
        d.numeric_value === null || d.numeric_value === undefined
          ? ""
          : String(d.numeric_value),
    }));
    setOptionDefaultRows(rows);
  };

  const fetchChannelDetail = async (id) => {
    setLoadingChannelDetail(true);
    try {
      const response = await axiosInstant.get(`/content-channels/${id}`);
      const data = response.data || {};
      hydrateOptionDefaults(data.option_defaults || []);
    } catch (err) {
      console.error("Failed to fetch channel detail:", err);
      hydrateOptionDefaults(initialData.option_defaults || []);
    } finally {
      setLoadingChannelDetail(false);
    }
  };

  useEffect(() => {
    if (open) {
      fetchContentTypes();
      fetchChannelOptions();
      setOptionDefaultsError("");
      setRowErrors({});
      if (initialData?.id) {
        fetchChannelDetail(initialData.id);
      } else {
        hydrateOptionDefaults(initialData?.option_defaults || []);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialData?.id]);

  const contentTypeOptions = contentTypes.map((type) => ({
    label: type.name,
    value: type.id,
  }));

  const selectedPairs = useMemo(
    () =>
      new Set(
        optionDefaultRows
          .filter((r) => r.optionId && r.valueId)
          .map((r) => `${r.optionId}:${r.valueId}`)
      ),
    [optionDefaultRows]
  );

  const updateRow = (index, patch) => {
    setOptionDefaultRows((prev) =>
      prev.map((r, i) => (i === index ? { ...r, ...patch } : r))
    );
    setRowErrors((prev) => {
      if (!prev[index]) return prev;
      const next = { ...prev };
      delete next[index];
      return next;
    });
  };

  const addOptionDefaultRow = () => {
    setOptionDefaultRows((prev) => [
      ...prev,
      {
        key: `new-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
        optionId: "",
        valueId: "",
        numericValue: "",
      },
    ]);
  };

  const removeOptionDefaultRow = (index) => {
    setOptionDefaultRows((prev) => prev.filter((_, i) => i !== index));
    setRowErrors((prev) => {
      if (!prev[index]) return prev;
      const next = { ...prev };
      delete next[index];
      return next;
    });
  };

  const buildOptionDefaultsPayload = () =>
    optionDefaultRows
      .filter((r) => r.valueId)
      .map((r) => ({
        content_channel_option_value_id: Number(r.valueId),
        numeric_value:
          r.numericValue === "" || r.numericValue === null
            ? null
            : Number(r.numericValue),
      }));

  const applyServerValidationError = (err) => {
    const data = err?.response?.data;
    const code = data?.code || data?.error || data?.errorCode;
    const message = data?.message || "";
    if (code === "InvalidNumericValue") {
      setOptionDefaultsError(
        message || "One of the numeric values is not a finite number."
      );
    } else if (code === "UnknownOptionValueId") {
      setOptionDefaultsError(
        message || "One of the selected option values no longer exists."
      );
    }
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box
        component={Paper}
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: { xs: "calc(100% - 24px)", sm: 560 },
          maxWidth: 560,
          maxHeight: { xs: "calc(100vh - 24px)", sm: "85vh" },
          bgcolor: "white",
          borderRadius: "12px",
          boxShadow: 24,
          padding: { xs: "16px", sm: "24px" },
          overflow: "auto",
        }}
      >
        <Formik
          validationSchema={validationSchema}
          initialValues={{
            name: initialData.name || "",
            autopilot_available: initialData.autopilot_available === true,
            content_type_ids:
              Array.isArray(initialData.content_types)
                ? initialData.content_types.map((ct) => ct.id)
                : Array.isArray(initialData.content_type_ids)
                  ? initialData.content_type_ids
                  : [],
          }}
          enableReinitialize
          onSubmit={async (values, { setSubmitting }) => {
            setOptionDefaultsError("");
            // Client-side numeric validation
            const nextRowErrors = {};
            optionDefaultRows.forEach((r, i) => {
              if (r.numericValue !== "" && r.numericValue !== null) {
                const n = Number(r.numericValue);
                if (!Number.isFinite(n)) {
                  nextRowErrors[i] = "Must be a finite number";
                }
              }
            });
            if (Object.keys(nextRowErrors).length > 0) {
              setRowErrors(nextRowErrors);
              setSubmitting(false);
              return;
            }

            const payload = defaultsOnly
              ? { option_defaults: buildOptionDefaultsPayload() }
              : {
                  ...values,
                  option_defaults: buildOptionDefaultsPayload(),
                };

            try {
              await onSave(payload);
            } catch (err) {
              applyServerValidationError(err);
            } finally {
              setSubmitting(false);
            }
          }}
        >
          {({ isValid, dirty, isSubmitting, values, setFieldValue }) => (
            <Form className="flex flex-col gap-4">
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <Typography sx={{ fontSize: "24px", fontWeight: 900 }}>
                  {defaultsOnly
                    ? "Edit Channel Defaults"
                    : `${initialData?.id ? "Edit" : "Add"} Content Channel`}
                </Typography>
                <IconButton onClick={onClose}>
                  <CloseIcon />
                </IconButton>
              </Box>

              <InputField
                name="name"
                placeholder="Enter channel name"
                label="Name"
                required
                type="text"
                disabled={defaultsOnly}
              />

              <FormControlLabel
                control={
                  <Switch
                    checked={values.autopilot_available}
                    onChange={(e) => setFieldValue("autopilot_available", e.target.checked)}
                    color="primary"
                    disabled={defaultsOnly}
                  />
                }
                label="Available for Autopilot (opens this channel and its placements to the video side)"
              />

              {loadingContentTypes ? (
                <Box display="flex" justifyContent="center" py={2}>
                  <CircularProgress size={24} />
                </Box>
              ) : (
                <CustomSelectField
                  name="content_type_ids"
                  label="Content Types"
                  required={false}
                  disabled={defaultsOnly}
                  options={contentTypeOptions}
                  placeholder="Select Content Types"
                  renderValue={(selected) => {
                    if (!selected || selected.length === 0) {
                      return "Select Content Types";
                    }
                    return `${selected.length} Content Type${
                      selected.length === 1 ? "" : "s"
                    } Selected`;
                  }}
                />
              )}

              <Box sx={{ mt: 2 }}>
                <Typography sx={{ fontSize: "16px", fontWeight: 700, mb: 1 }}>
                  Option defaults
                </Typography>

                {(loadingChannelOptions || loadingChannelDetail) && (
                  <Box display="flex" justifyContent="center" py={2}>
                    <CircularProgress size={20} />
                  </Box>
                )}

                {optionDefaultsError && (
                  <FormHelperText error sx={{ mb: 1 }}>
                    {optionDefaultsError}
                  </FormHelperText>
                )}

                <Box display="flex" flexDirection="column" gap={1.5}>
                  {optionDefaultRows.map((row, index) => {
                    const selectedOption = channelOptions.find(
                      (o) => o.id === Number(row.optionId)
                    );
                    const values = selectedOption?.values || [];
                    const showNumericValue = selectedOption?.type === "value";
                    return (
                      <Box
                        key={row.key}
                        display="flex"
                        gap={1}
                        alignItems="flex-start"
                      >
                        <FormControl size="small" sx={{ minWidth: 160, flex: 1 }}>
                          <InputLabel>Option</InputLabel>
                          <Select
                            label="Option"
                            value={row.optionId || ""}
                            onChange={(e) =>
                              updateRow(index, {
                                optionId: e.target.value,
                                valueId: "",
                              })
                            }
                          >
                            {channelOptions.map((opt) => (
                              <MenuItem key={opt.id} value={opt.id}>
                                {opt.name}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>

                        <FormControl
                          size="small"
                          sx={{ minWidth: 160, flex: 1 }}
                          disabled={!row.optionId}
                        >
                          <InputLabel>Value</InputLabel>
                          <Select
                            label="Value"
                            value={row.valueId || ""}
                            onChange={(e) =>
                              updateRow(index, { valueId: e.target.value })
                            }
                          >
                            {values.map((v) => {
                              const pairKey = `${row.optionId}:${v.id}`;
                              const isCurrent =
                                Number(row.valueId) === Number(v.id);
                              const isUsed =
                                !isCurrent && selectedPairs.has(pairKey);
                              return (
                                <MenuItem
                                  key={v.id}
                                  value={v.id}
                                  disabled={isUsed}
                                >
                                  {v.name}
                                </MenuItem>
                              );
                            })}
                          </Select>
                        </FormControl>

                        {showNumericValue && (
                          <FormControl
                            size="small"
                            sx={{ width: 130 }}
                            error={!!rowErrors[index]}
                          >
                            <TextField
                              size="small"
                              label="Numeric value"
                              type="number"
                              inputProps={{ step: "any" }}
                              value={row.numericValue}
                              onChange={(e) =>
                                updateRow(index, {
                                  numericValue: e.target.value,
                                })
                              }
                              error={!!rowErrors[index]}
                            />
                            {rowErrors[index] && (
                              <FormHelperText error>
                                {rowErrors[index]}
                              </FormHelperText>
                            )}
                          </FormControl>
                        )}

                        <IconButton
                          aria-label="Remove default"
                          onClick={() => removeOptionDefaultRow(index)}
                          size="small"
                          sx={{ mt: "4px" }}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    );
                  })}
                </Box>

                <Button
                  type="button"
                  onClick={addOptionDefaultRow}
                  startIcon={<AddIcon />}
                  size="small"
                  sx={{ mt: 1, textTransform: "none" }}
                  disabled={loadingChannelOptions}
                >
                  Add default
                </Button>
              </Box>

              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: 2,
                  mt: 1,
                }}
              >
                <Button onClick={onClose} type="button">
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={isSubmitting || !isValid}
                >
                  {initialData?.id ? "Save" : "Add"}
                </Button>
              </Box>
            </Form>
          )}
        </Formik>
      </Box>
    </Modal>
  );
};

export default ContentChannelModal;
